import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'

import { AiAnalyticsFilter, AiAnalyticsResponse, AiSuggestion, AiSuggestionReview, getAiAnalytics } from 'core'
import { now } from 'core/application/commons/dateUtils'
import getFileUrl from 'core/application/commons/fileStorage/getFileUrl'
import PagedList from 'core/application/commons/pagedList'

import { ROUTE_PATHS } from 'common/routePaths'

import { queryRequest } from '../../common/RequestUtils'
import { Card, Col, DatePicker, DefaultRadioGroupV2, DefaultSelectV2, Icon, Row } from '../../common/components'
import Input from '../../common/components/dataEntry/components/Input'
import CheckboxV2 from '../../common/components/dataEntry/formik/CheckboxV2'
import notification from '../../common/components/feedback/Notification'
import Button from '../../common/components/general/Button'
import Pagination from '../../common/components/navigation/Pagination'
import { formatDateTimeAMPM, parseZoneFormat } from '../../common/dateFormatters'
import { EXPENSES_PATH } from '../Expenses'
import { JSON_PATH } from './JsonContent'

export const AI_ANALYTICS_PATH = 'ai-analytics'

const AiAnalytics = () => {
  const { t: translate } = useTranslation()
  const [searchValue, setSearchValue] = useState<string>(null)

  const [filter, setFilter] = useState<AiAnalyticsFilter>({
    aiSuggestionReview: null,
    pageNumber: 1,
    pageSize: 10,
    orderColumn: 'expenseNumber',
    orderDescending: true,
    aiSuggestion: AiSuggestion.Verify,
    expenseTypeId: null,
    reviewedOnly: false,
    ineligibleExpenses: false,
    selectedExpenseTypeId: null,
    date: false,
    notDate: false,
    amount: false,
    notAmount: false,
    rx: false,
    notRx: false,
    hsa: false,
    notHsa: false,
    dateAndAmount: false,
    notDateAndAmount: false,
    amountAndRx: false,
    notAmountAndRx: false,
    verified: false,
    notVerified: false,
    eyeByeDirect: false,
    notEyeByeDirect: false,
    one800Contact: false,
    notOne800Contact: false,
    copay: false,
    notCopay: false,
    suggestionDuplicate: false,
    notSuggestionDuplicate: false,
    autoVerified: false,
    notAutoVerified: false,
    notVerifiedReason: false,
    noReason: false,
  })

  const [initialValues, setInitialValues] = useState<{
    aiSuggestionReview: AiSuggestionReview | null
    aiSuggestion: AiSuggestion | null
    prescriptionDrugs: boolean
    reviewedOnly: boolean
    startDate: string | null
    ineligibleExpenses: boolean
    selectedExpenseTypeId: number | null
    date: boolean
    notDate: boolean
    amount: boolean
    notAmount: boolean
    rx: boolean
    notRx: boolean
    hsa: boolean
    notHsa: boolean
    dateAndAmount: boolean
    notDateAndAmount: boolean
    amountAndRx: boolean
    notAmountAndRx: boolean
    verified: boolean
    notVerified: boolean
    eyeByeDirect: boolean
    notEyeByeDirect: boolean
    one800Contact: boolean
    notOne800Contact: boolean
    copay: boolean
    notCopay: boolean
    suggestionDuplicate: boolean
    notSuggestionDuplicate: boolean
    autoVerified: boolean
    notAutoVerified: boolean
    notVerifiedReason: boolean
    noReason: boolean
  }>({
    aiSuggestionReview: null,
    aiSuggestion: AiSuggestion.Verify,
    prescriptionDrugs: false,
    reviewedOnly: false,
    startDate: null,
    ineligibleExpenses: false,
    selectedExpenseTypeId: null,
    date: false,
    notDate: false,
    amount: false,
    notAmount: false,
    rx: false,
    notRx: false,
    hsa: false,
    notHsa: false,
    dateAndAmount: false,
    notDateAndAmount: false,
    amountAndRx: false,
    notAmountAndRx: false,
    verified: false,
    notVerified: false,
    eyeByeDirect: false,
    notEyeByeDirect: false,
    one800Contact: false,
    notOne800Contact: false,
    copay: false,
    notCopay: false,
    suggestionDuplicate: false,
    notSuggestionDuplicate: false,
    autoVerified: false,
    notAutoVerified: false,
    notVerifiedReason: false,
    noReason: false,
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [expenses, setExpenses] = useState<PagedList<AiAnalyticsResponse>>(null)

  useEffect(() => {
    ;(async () => {
      if (filter) {
        setIsLoading(true)
        const response = await getAiAnalytics(filter)
        setExpenses(response)
        setIsLoading(false)
      }
    })()
  }, [filter])

  const getDocumentUrl = async (filename: string) => {
    const result = await queryRequest(() => getFileUrl(filename))
    if (!result.errors) {
      return result
    } else {
      notification.open({
        type: 'error',
        message: result.errors[0].message,
      })
      return ''
    }
  }

  const onSearch = (q: string) => {
    setFilter({
      ...filter,
      searchValue: q,
      pageNumber: 1,
    })
  }

  const search = (e: any) => {
    const value = e.target.value
    setSearchValue(value)
    if (e.keyCode == 13) {
      onSearch(value !== '' ? value : null)
    } else if (value === '') {
      onSearch(null)
    }
  }

  const handlePageChange = (pageNumber: number, pageSize: number) => {
    setFilter({ ...filter, pageNumber: pageNumber, pageSize: pageSize })
  }

  if (isLoading) return <div>Loading...</div>
  return (
    <>
      {initialValues && (
        <Formik enableReinitialize={true} initialValues={initialValues} onSubmit={() => {}}>
          {(formik) => {
            return (
              <>
                <form onSubmit={formik.handleSubmit}>
                  <Row>
                    <Col span={6}>
                      <Row>
                        <Col span={12}>Field</Col>
                        <Col span={6}>AI Match</Col>
                        <Col span={6}>Not Match</Col>
                      </Row>
                      <Row className={'mt-24'}>
                        <Col span={12}>Date</Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'date'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                date: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, date: event })
                            }}
                          />
                        </Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'notDate'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                notDate: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, notDate: event })
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col span={12}>Amount</Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'amount'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                amount: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, amount: event })
                            }}
                          />
                        </Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'notAmount'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                notAmount: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, notAmount: event })
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col span={12}>Rx</Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'rx'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                rx: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, rx: event })
                            }}
                          />
                        </Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'notRx'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                notRx: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, notRx: event })
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col span={12}>HSA</Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'hsa'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                hsa: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, hsa: event })
                            }}
                          />
                        </Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'notHsa'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                notHsa: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, notHsa: event })
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col span={12}>Date and Amount</Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'dateAndAmount'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                dateAndAmount: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, dateAndAmount: event })
                            }}
                          />
                        </Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'notDateAndAmount'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                notDateAndAmount: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, notDateAndAmount: event })
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col span={12}>Amount and Rx</Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'amountAndRx'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                amountAndRx: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, amountAndRx: event })
                            }}
                          />
                        </Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'notAmountAndRx'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                notAmountAndRx: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, notAmountAndRx: event })
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col span={12}>Verified</Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'verified'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                verified: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, verified: event })
                            }}
                          />
                        </Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'notVerified'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                notVerified: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, notVerified: event })
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col span={12}>Eye Buy Direct</Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'eyeByeDirect'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                eyeByeDirect: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, eyeByeDirect: event })
                            }}
                          />
                        </Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'notEyeByeDirect'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                notEyeByeDirect: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, notEyeByeDirect: event })
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col span={12}>1800 Contact</Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'one800Contact'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                one800Contact: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, one800Contact: event })
                            }}
                          />
                        </Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'notOne800Contact'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                notOne800Contact: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, notOne800Contact: event })
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col span={12}>Copay</Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'copay'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                copay: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, copay: event })
                            }}
                          />
                        </Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'notCopay'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                notCopay: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, notCopay: event })
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col span={12}>Suggestion Duplicate</Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'suggestionDuplicate'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                suggestionDuplicate: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, suggestionDuplicate: event })
                            }}
                          />
                        </Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'notSuggestionDuplicate'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                notSuggestionDuplicate: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, notSuggestionDuplicate: event })
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col span={12}>Auto Verified</Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'autoVerified'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                autoVerified: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, autoVerified: event })
                            }}
                          />
                        </Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'notAutoVerified'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                notAutoVerified: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, notAutoVerified: event })
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col span={12}>Not Verified Reason</Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'notVerifiedReason'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                notVerifiedReason: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, notVerifiedReason: event })
                            }}
                          />
                        </Col>
                        <Col span={6}>
                          <CheckboxV2
                            name={'noReason'}
                            checked={false}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                noReason: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, noReason: event })
                            }}
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col span={6}>
                      <Row>
                        <div className="flex flex__space_between">
                          <Input
                            suffix={searchIcon}
                            placeholder={'Search expense number'}
                            onKeyUp={search}
                            className="mr-8 width_100"
                            defaultValue={searchValue}
                            allowClear
                            onChange={search}
                          />
                          <Button type="primary" onClick={() => onSearch(searchValue)}>
                            {translate('general.search')}
                          </Button>
                        </div>
                      </Row>
                      <Row className={'mt-24'}>
                        <Col span={24}>
                          <DatePicker
                            name="startDate"
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                startDate: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, startDate: event })
                            }}
                            className="width_50"
                            disabledDate={(d) => {
                              return !d || d.isAfter(now())
                            }}
                          ></DatePicker>
                        </Col>
                      </Row>
                      <Row className={'mt-24'}>
                        <Col span={24}>
                          <DefaultSelectV2
                            name={'selectedExpenseTypeId'}
                            // label={'Select Expense Type'}
                            placeholder={'Select Expense Type'}
                            className="width_50"
                            items={expenseTypes}
                            allowClear={true}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                selectedExpenseTypeId: event,
                                pageNumber: 1,
                              })
                              setInitialValues({ ...initialValues, selectedExpenseTypeId: event })
                            }}
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col span={6}>
                      <Row>
                        <DefaultRadioGroupV2
                          name={'aiSuggestionReview'}
                          label={'AI Review:'}
                          direction="horizontal"
                          onChange={(event) => {
                            setFilter({ ...filter, aiSuggestionReview: event.target.value, pageNumber: 1 })
                            setInitialValues({ ...initialValues, aiSuggestionReview: event.target.value })
                          }}
                          items={[
                            { name: 'Agree', value: AiSuggestionReview.Positive },
                            { name: 'AWC', value: AiSuggestionReview.AgreeWithConditions },
                            { name: 'Disagree', value: AiSuggestionReview.Negative },
                            { name: 'No Decision', value: AiSuggestionReview.NoDecision },
                          ]}
                        />
                      </Row>
                      <Row className={'mt-24'}>
                        <DefaultRadioGroupV2
                          name={'aiSuggestion'}
                          label={'AI Suggestion:'}
                          direction="horizontal"
                          onChange={(event) => {
                            setFilter({
                              ...filter,
                              aiSuggestion: event.target.value,
                              pageNumber: 1,
                            })
                            setInitialValues({ ...initialValues, aiSuggestion: event.target.value })
                          }}
                          items={[
                            { name: 'Verify', value: AiSuggestion.Verify },
                            { name: 'Deny', value: AiSuggestion.Deny },
                            { name: 'Not Enough Info', value: AiSuggestion.NotEnoughInfo },
                            { name: 'Any', value: null },
                          ]}
                        />
                      </Row>
                    </Col>
                    <Col span={6}>
                      <Row className={'mt-24'}>
                        <CheckboxV2
                          name={'reviewedOnly'}
                          label={'Reviewed Only'}
                          checked={false}
                          onChange={(event) => {
                            setFilter({
                              ...filter,
                              reviewedOnly: event,
                              pageNumber: 1,
                            })
                            setInitialValues({ ...initialValues, reviewedOnly: event })
                          }}
                        />
                      </Row>
                      <Row className={'mt-24'}>
                        <CheckboxV2
                          name={'ineligibleExpenses'}
                          label={'Ineligible Expenses Only'}
                          checked={false}
                          onChange={(event) => {
                            setFilter({
                              ...filter,
                              ineligibleExpenses: event,
                              pageNumber: 1,
                            })
                            setInitialValues({ ...initialValues, ineligibleExpenses: event })
                          }}
                        />
                      </Row>
                    </Col>
                  </Row>
                  {expenses && <div className={'mt-24'}>Total items: {expenses.totalItemCount}</div>}

                  {expenses?.items.map((expense) => (
                    <Card
                      title={
                        <div>
                          Expense number:{' '}
                          <a
                            target={'_blank'}
                            onClick={() =>
                              window.open(
                                `${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/${EXPENSES_PATH}/${expense.expenseId}`,
                                '_blank',
                              )
                            }
                          >
                            {expense.expenseNumber}
                          </a>
                        </div>
                      }
                    >
                      <Row>
                        <Col span={8}>
                          <Row>
                            Ai Review:{' '}
                            {expense.aiSuggestionReview === AiSuggestionReview.NoDecision
                              ? 'No Decision'
                              : expense.aiSuggestionReview === AiSuggestionReview.Positive
                              ? 'Agree'
                              : 'Disagree'}
                          </Row>
                          <Row>AI Suggestion: {expense.aiSuggestion.name}</Row>
                          {expense.attachments.filter((x) => x.duplicatedExpenseNumber).length > 0 && (
                            <Row>
                              Duplicate expense :{' '}
                              {expense.attachments.filter((x) => x.duplicatedExpenseNumber)[0].duplicatedExpenseNumber}
                            </Row>
                          )}
                          <Row>Requested Amount: $ {expense.requestedAmount}</Row>
                          <Row>Date Of Service: {parseZoneFormat(expense.dateOfService)}</Row>
                          <Row>Service Provider: {expense.serviceProvider}</Row>
                          <Row>Receiver Name: {expense.receiverName}</Row>
                          <Row>Requested Expense Type: {expense.expenseType}</Row>
                          <Row>Last Review: {expense.lastReviewAt ? parseZoneFormat(expense.lastReviewAt) : '-'}</Row>
                          <Row>Is Auto-verified: {expense.isAutoVerified ? 'Yes' : 'No'}</Row>
                        </Col>
                        <Col span={16}>
                          {expense.attachments &&
                            expense.attachments.map((attachment) => (
                              <Row>
                                <Col span={8} className={'align-start mt-24'}>
                                  <Row>
                                    <a
                                      style={{ wordWrap: 'break-word', verticalAlign: 'top' }}
                                      onClick={async (e) => {
                                        e.stopPropagation()
                                        const documentUrl = await getDocumentUrl(attachment.filename)
                                        if (documentUrl) window.open(documentUrl, '_blank')
                                      }}
                                      target="_blank"
                                    >
                                      {attachment.title}
                                      {attachment.createdAt ? `(${formatDateTimeAMPM(attachment.createdAt)})` : ''}
                                    </a>
                                  </Row>
                                  {attachment.jsonFileName && (
                                    <Row className={'mt-24'}>
                                      <a
                                        style={{ wordWrap: 'break-word', verticalAlign: 'top' }}
                                        onClick={async (e) => {
                                          window.open(
                                            `${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/${AI_ANALYTICS_PATH}/${JSON_PATH}?fileName=${attachment.jsonFileName}`,
                                            '_blank',
                                          )
                                        }}
                                      >
                                        Extracted Info
                                      </a>
                                    </Row>
                                  )}
                                </Col>
                                <Col span={16} className={'mt-24'}>
                                  {attachment.extractExpenseDataFromFile &&
                                    attachment.extractExpenseDataFromFile.items.length > 0 && (
                                      <>
                                        <Row className={'color-gray-10'}>
                                          <Col span={24}>
                                            <b>Date of Service Match:</b>{' '}
                                            {attachment.extractExpenseDataFromFile.dateOfServiceMatch ? 'Yes' : 'No'}
                                          </Col>
                                        </Row>
                                        <Row className={'color-gray-10'}>
                                          <Col span={24}>
                                            <b>Amount submitted Match:</b>{' '}
                                            {attachment.extractExpenseDataFromFile.amountSubmittedMatch ? 'Yes' : 'No'}
                                          </Col>
                                        </Row>
                                        {attachment.notVerifiedReason.id !== 0 && (
                                          <Row className={'mb-16 color-gray-10'}>
                                            <Col span={24}>
                                              <b>Reason To Not Suggest Verify:</b> {attachment.notVerifiedReason.name}
                                            </Col>
                                          </Row>
                                        )}
                                        {attachment.extractExpenseDataFromFile.items.map((item) => (
                                          <Row className={'color-gray-10 mt-16'}>
                                            {item.vendor && (
                                              <Col span={24}>
                                                <b>Vendor:</b> {item.vendor} ({item.vendorConfidence}%)
                                              </Col>
                                            )}
                                            {item.otherVendors &&
                                              item.otherVendors.map((v) => (
                                                <Col span={24}>
                                                  <b>Vendor:</b> {v.label} - {v.value}
                                                </Col>
                                              ))}
                                            {item.amountPaid && (
                                              <Col span={24}>
                                                <b>Amount Paid:</b> {item.amountPaid} ({item.amountPaidConfidence}%)
                                              </Col>
                                            )}
                                            {item.otherAmounts &&
                                              item.otherAmounts.map((v) => (
                                                <Col span={24}>
                                                  <b>Total:</b> {v.label} - {v.value}
                                                </Col>
                                              ))}
                                            {item.receiverName && (
                                              <Col span={24}>
                                                <b>Receiver Name:</b> {item.receiverName} ({item.receiverNameConfidence}
                                                %)
                                              </Col>
                                            )}
                                            {item.otherReceiverNames &&
                                              item.otherReceiverNames.map((v) => (
                                                <Col span={24}>
                                                  <b>Receiver Name:</b> {v.label} - {v.value}
                                                </Col>
                                              ))}
                                            {item.otherNames &&
                                              item.otherNames.map((v) => (
                                                <Col span={24}>
                                                  <b>Name:</b> {v.label} - {v.value}
                                                </Col>
                                              ))}
                                            {item.invoiceReceiptDate && (
                                              <Col span={24}>
                                                <b>Invoice Receipt Date:</b> {item.invoiceReceiptDate} (
                                                {item.invoiceReceiptDateConfidence}%)
                                              </Col>
                                            )}
                                            {item.otherDates &&
                                              item.otherDates.map((v) => (
                                                <Col span={24}>
                                                  <b>Date of Service:</b> {v.label} - {v.value}
                                                </Col>
                                              ))}
                                            {item.hsa &&
                                              item.hsa.map((v) => (
                                                <Col span={24}>
                                                  <b>HSA item:</b> {v.label} - {v.value}
                                                </Col>
                                              ))}
                                            {item.specificHsa &&
                                              item.specificHsa.map((v) => (
                                                <Col span={24}>
                                                  <b>HSA item:</b> {v.label} - {v.value}
                                                </Col>
                                              ))}
                                            {item.specificHsa && (
                                              <Col span={24}>
                                                <b>HSA amount match:</b> {item.hsaAmountMatch ? 'Yes' : 'No'}
                                              </Col>
                                            )}
                                            {item.specificHsa && (
                                              <Col span={24}>
                                                <b>HSA date match:</b> {item.hsaDateMatch ? 'Yes' : 'No'}
                                              </Col>
                                            )}
                                            {item.rx &&
                                              item.rx.map((v) => (
                                                <Col span={24}>
                                                  <b>RX item:</b> {v.label} - {v.value}
                                                </Col>
                                              ))}
                                            {item.neiFields &&
                                              item.neiFields.map((v) => (
                                                <Col span={24}>
                                                  <b>Nei keywords:</b> {v.label} - {v.value}
                                                </Col>
                                              ))}
                                            {item.denyFields &&
                                              item.denyFields.map((v) => (
                                                <Col span={24}>
                                                  <b>Deny keywords:</b> {v.label} - {v.value}
                                                </Col>
                                              ))}
                                          </Row>
                                        ))}
                                      </>
                                    )}
                                </Col>
                              </Row>
                            ))}
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  {expenses && (
                    <Pagination
                      defaultPage={expenses.pageNumber}
                      defaultPageSize={expenses.pageSize}
                      total={expenses.totalItemCount}
                      onChange={handlePageChange}
                      hideOnSinglePage={true}
                    />
                  )}
                </form>
              </>
            )
          }}
        </Formik>
      )}
    </>
  )
}

const searchIcon = <Icon type="fal fa-search" fontAwesome />

const expenseTypes = [
  { id: 196, name: 'Prescription Drugs' },
  { id: 177, name: 'Doctor visits (medical)' },
  { id: 161, name: 'Mental health counseling' },
  { id: 33, name: 'Chiropractic care' },
  { id: 58, name: 'Dental care (non-cosmetic)' },
  { id: 190, name: 'Physical therapy' },
  { id: 84, name: 'Eyeglasses (prescription)' },
  { id: 77, name: 'Drugs and medicines (over-the-counter)' },
  { id: 180, name: 'Orthodontia (braces and retainers)' },
  { id: 130, name: 'Laboratory fees' },
  { id: 197, name: 'Psychiatric / therapy' },
  { id: 90, name: 'Menstrual care products' },
  { id: 42, name: 'Contact lenses and solutions' },
  { id: 86, name: 'Eye examinations' },
  { id: 6, name: 'Allergy and sinus medicine and products' },
]

export default AiAnalytics
